import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { User } from "@/lib/api/user";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/utils";
import { MentorWithSlot, useSimulateMentorMatch } from "@/lib/api/onboarding";
import { Loader2 } from "lucide-react";
import {
  interestsOptions,
  struggledWithOptions,
  mostLikeYouOptions,
  sexOptions,
  raceOptions,
} from "@/lib/strings/profile-data";
import { MentorDetailInfo } from "./mentor-detail-info";
import { useGenerateMentorSubheading } from "@/lib/api/admin";
import { useNavigate } from "@tanstack/react-router";

// Form schema
const formSchema = z.object({
  algorithmVersion: z.enum(["v1", "v2"]).default("v2"),
  teen: z.object({
    sex: z.string(),
    mentorPreferences: z.object({
      // V1 Parameters
      race: z.string().optional(),
      helpWith: z.array(z.string()).optional(),
      // V2 Parameters
      interests: z.array(z.string()).optional(),
      topInterest: z.string().optional(),
      lifeExperiences: z.array(z.string()).optional(),
      personalityTraits: z.array(z.string()).optional(),
      useV2Algorithm: z.boolean().optional(),
    }),
  }),
});

type FormValues = z.infer<typeof formSchema>;

export function MentorMatchingSimulator() {
  const [isOpen, setIsOpen] = useState(false);
  const [matchedMentors, setMatchedMentors] = useState<
    Array<{
      mentor: MentorWithSlot;
      score: number;
      scoreBreakdown?: {
        // V1 breakdown
        race?: number;
        struggles?: number;
        interests?: number;
        // V2 breakdown
        v2_breakdown?: {
          interestMatch: number;
          experienceMatch: number;
          personalityMatch: number;
          matchRateWeight: number;
          fairnessWeight: number;
          finalScore: number;
        };
        match_rate_data?: {
          mentorId: string;
          matchRate: number;
          hasMinimumSample: boolean;
          impressions: number;
          selections: number;
          skips: number;
        };
        weekly_impressions?: number;
      };
    }>
  >([]);

  const simulateMentorMatch = useSimulateMentorMatch();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      algorithmVersion: "v2" as const,
      teen: {
        sex: "",
        mentorPreferences: {
          // V1 Parameters
          race: "",
          helpWith: [],
          // V2 Parameters
          interests: [],
          topInterest: "",
          lifeExperiences: [],
          personalityTraits: [],
          useV2Algorithm: true,
        },
      },
    },
  });

  const onSubmit = async (data: FormValues) => {
    console.log("onSubmit", data);
    try {
      // Prepare request based on algorithm version
      const requestData = {
        teen: {
          sex: data.teen.sex,
          mentorPreferences:
            data.algorithmVersion === "v2"
              ? {
                  // V2 Parameters
                  interests: data.teen.mentorPreferences.interests,
                  topInterest: data.teen.mentorPreferences.topInterest,
                  lifeExperiences: data.teen.mentorPreferences.lifeExperiences,
                  personalityTraits:
                    data.teen.mentorPreferences.personalityTraits,
                  useV2Algorithm: true,
                }
              : {
                  // V1 Parameters
                  race: data.teen.mentorPreferences.race,
                  helpWith: data.teen.mentorPreferences.helpWith,
                },
        },
      };

      const result = await simulateMentorMatch.mutateAsync(requestData);
      console.log("result", result);
      setMatchedMentors(result.simulateMatches);
    } catch (error) {
      console.error("Error simulating mentor match:", error);
    }
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>Test Mentor Matching</Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Mentor Matching Simulator</DialogTitle>
            <DialogDescription>
              Simulate teen onboarding answers to see which mentors would match.
              Only top 3 matches are shown to the user during onboarding. If a
              mentor is not showing up here, it means their profile is not
              complete, or they have no available slots left for the day or at
              all.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="algorithmVersion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Algorithm Version</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-row space-x-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="v1" id="v1" />
                              <Label htmlFor="v1">V1 (Legacy)</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="v2" id="v2" />
                              <Label htmlFor="v2">V2 (New)</Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="teen.sex"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teen&apos;s Biological Sex</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="rounded-xl">
                              <SelectValue placeholder="Select sex" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {sexOptions.map(({ value, label }) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* V1 Help With field */}
                  {form.watch("algorithmVersion") === "v1" && (
                    <FormField
                      control={form.control}
                      name="teen.mentorPreferences.helpWith"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Help With (Interests & Struggles) - V1
                          </FormLabel>
                          <MultiSelect
                            options={[
                              ...interestsOptions,
                              ...struggledWithOptions,
                            ]}
                            defaultValue={
                              field.value?.map((value) => ({
                                value,
                                label: value,
                              })) || []
                            }
                            onChange={(options) => {
                              const values = options.map(
                                (option) => option.value,
                              );
                              field.onChange(values);
                            }}
                            className="w-full"
                            placeholder="Select interests and struggles..."
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* V1-specific fields */}
                  {form.watch("algorithmVersion") === "v1" && (
                    <FormField
                      control={form.control}
                      name="teen.mentorPreferences.race"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preferred Mentor Race (V1)</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder="Select race" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {raceOptions.map(({ value, label }) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* V2-specific fields */}
                  {form.watch("algorithmVersion") === "v2" && (
                    <>
                      <FormField
                        control={form.control}
                        name="teen.mentorPreferences.interests"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Interests (V2)</FormLabel>
                            <MultiSelect
                              options={interestsOptions}
                              defaultValue={
                                field.value?.map((value) => ({
                                  value,
                                  label: value,
                                })) || []
                              }
                              onChange={(options) => {
                                const values = options.map(
                                  (option) => option.value,
                                );
                                field.onChange(values);
                              }}
                              className="w-full"
                              placeholder="Select interests..."
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="teen.mentorPreferences.topInterest"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Top Interest (Priority)</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="rounded-xl">
                                  <SelectValue placeholder="Select top interest" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {form
                                  .watch("teen.mentorPreferences.interests")
                                  ?.map((interest) => (
                                    <SelectItem key={interest} value={interest}>
                                      {interest}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="teen.mentorPreferences.lifeExperiences"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Life Experiences (V2)</FormLabel>
                            <MultiSelect
                              options={struggledWithOptions}
                              defaultValue={
                                field.value?.map((value) => ({
                                  value,
                                  label: value,
                                })) || []
                              }
                              onChange={(options) => {
                                const values = options.map(
                                  (option) => option.value,
                                );
                                field.onChange(values);
                              }}
                              className="w-full"
                              placeholder="Select life experiences..."
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="teen.mentorPreferences.personalityTraits"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Personality Traits</FormLabel>
                            <MultiSelect
                              options={mostLikeYouOptions}
                              defaultValue={
                                field.value?.map((value) => ({
                                  value,
                                  label: value,
                                })) || []
                              }
                              onChange={(options) => {
                                const values = options.map(
                                  (option) => option.value,
                                );
                                field.onChange(values);
                              }}
                              className="w-full"
                              placeholder="Select personality traits..."
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={simulateMentorMatch.isPending}
                    >
                      {simulateMentorMatch.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Find Matches
                    </Button>
                    {/* <Button
                      type="button"
                      variant="outline"
                      onClick={loadSampleData}
                    >
                      Load Sample Data
                    </Button> */}
                  </div>
                </form>
              </Form>
            </div>

            <div>
              <h3 className="mb-4 text-lg font-medium">Matched Mentors</h3>
              {matchedMentors?.length === 0 ? (
                <p>No matches found. Try adjusting your criteria.</p>
              ) : (
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 max-h-[600px] space-y-4 overflow-y-auto pr-2">
                  <p className="text-muted-foreground mb-2 text-sm">
                    Showing {matchedMentors.length}/10 potential matches, sorted
                    by match score
                  </p>
                  {matchedMentors
                    .sort((a, b) => b.score - a.score)
                    .map(({ mentor, score, scoreBreakdown }, index) => (
                      <MentorCard
                        key={mentor.id}
                        mentor={mentor}
                        score={score}
                        scoreBreakdown={scoreBreakdown}
                        rank={index + 1}
                        form={form}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface MentorCardProps {
  mentor: MentorWithSlot;
  score: number;
  scoreBreakdown?: {
    // V1 breakdown
    race?: number;
    struggles?: number;
    interests?: number;
    // V2 breakdown
    v2_breakdown?: {
      interestMatch: number;
      experienceMatch: number;
      personalityMatch: number;
      matchRateWeight: number;
      fairnessWeight: number;
      finalScore: number;
    };
    match_rate_data?: {
      mentorId: string;
      matchRate: number;
      hasMinimumSample: boolean;
      impressions: number;
      selections: number;
      skips: number;
    };
    weekly_impressions?: number;
  };
  rank: number;
  form: UseFormReturn<FormValues>;
}

function MentorCard({
  mentor,
  score,
  scoreBreakdown,
  rank,
  form,
}: MentorCardProps) {
  const [showDetail, setShowDetail] = useState(false);
  const [generatedSubheading, setGeneratedSubheading] = useState<string | null>(
    null,
  );
  const generateSubheading = useGenerateMentorSubheading();
  const navigate = useNavigate();

  const calculateRemainingSlots = (
    mentorSlot: {
      max_daily_slots: number;
      slots_used_today: number;
      max_total_slots: number;
    } | null,
  ) => {
    if (!mentorSlot) return 0;

    // Calculate remaining slots for today
    const dailyRemaining =
      mentorSlot.max_daily_slots - mentorSlot.slots_used_today;
    // Calculate total remaining slots
    const totalRemaining = mentorSlot.max_total_slots;

    // Return the lower of the two limits
    return Math.min(dailyRemaining, totalRemaining);
  };

  const handleGenerateSubheading = async () => {
    try {
      const teenInterests =
        form.getValues("teen.mentorPreferences.helpWith") || [];
      const mentorInterests = mentor.profile?.interests || [];

      const result = await generateSubheading.mutateAsync({
        teenInterests,
        mentorInterests,
      });

      setGeneratedSubheading(result.subheading);
    } catch (error) {
      console.error("Failed to generate subheading:", error);
    }
  };

  return (
    <Card className="relative">
      <div className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium">
        #{rank}
      </div>
      <CardHeader className="flex flex-row items-center gap-4 pb-2">
        <Avatar className="h-12 w-12">
          <AvatarImage
            src={mentor?.profile_picture_url ?? undefined}
            alt={mentor.name ?? ""}
          />
          <AvatarFallback>
            {getInitials(mentor.name ?? undefined)}
          </AvatarFallback>
        </Avatar>
        <div>
          <CardTitle className="text-base">{mentor.name}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-1 text-sm">
        {mentor?.mentorSlot && (
          <div className="bg-muted/50 flex items-center justify-between rounded-lg p-3">
            <span className="flex items-center gap-2">
              <span role="img" aria-label="slots">
                📅
              </span>
              <span>Slots Available Today:</span>
            </span>
            <span className="font-medium">
              {calculateRemainingSlots(mentor?.mentorSlot)}
            </span>
          </div>
        )}

        {scoreBreakdown && (
          <div className="bg-muted rounded-lg p-3">
            <p className="mb-2 font-medium">
              Match Score Breakdown{" "}
              {scoreBreakdown.v2_breakdown ? "(V2)" : "(V1)"}:
            </p>
            <div className="space-y-2">
              {/* V1 Breakdown */}
              {!scoreBreakdown.v2_breakdown && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="race">
                        👥
                      </span>
                      <span>Race Match</span>
                    </div>
                    <span className="font-medium">
                      {scoreBreakdown.race || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="struggles">
                        🤝
                      </span>
                      <span>Struggles Match</span>
                    </div>
                    <span className="font-medium">
                      {scoreBreakdown.struggles || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="interests">
                        ⭐
                      </span>
                      <span>Interests Match</span>
                    </div>
                    <span className="font-medium">
                      {scoreBreakdown.interests || 0}
                    </span>
                  </div>
                </>
              )}

              {/* V2 Breakdown */}
              {scoreBreakdown.v2_breakdown && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="interests">
                        🎯
                      </span>
                      <span>Interest Match</span>
                    </div>
                    <span className="font-medium">
                      {(
                        scoreBreakdown.v2_breakdown.interestMatch * 100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="experiences">
                        💙
                      </span>
                      <span>Experience Match</span>
                    </div>
                    <span className="font-medium">
                      {(
                        scoreBreakdown.v2_breakdown.experienceMatch * 100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="personality">
                        🧠
                      </span>
                      <span>Personality Match</span>
                    </div>
                    <span className="font-medium">
                      {(
                        scoreBreakdown.v2_breakdown.personalityMatch * 100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="performance">
                        📊
                      </span>
                      <span>Match Rate</span>
                    </div>
                    <span className="font-medium">
                      {(
                        scoreBreakdown.v2_breakdown.matchRateWeight * 100
                      ).toFixed(0)}
                      %
                      {scoreBreakdown.match_rate_data && (
                        <span className="text-muted-foreground ml-1 text-xs">
                          ({scoreBreakdown.match_rate_data.matchRate}%)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span role="img" aria-label="fairness">
                        ⚖️
                      </span>
                      <span>Fairness</span>
                    </div>
                    <span className="font-medium">
                      {(
                        scoreBreakdown.v2_breakdown.fairnessWeight * 100
                      ).toFixed(0)}
                      %
                      {scoreBreakdown.weekly_impressions !== undefined && (
                        <span className="text-muted-foreground ml-1 text-xs">
                          ({scoreBreakdown.weekly_impressions} views)
                        </span>
                      )}
                    </span>
                  </div>
                </>
              )}

              <div className="border-border mt-3 flex items-center justify-between border-t pt-2">
                <span className="font-medium">Overall Score</span>
                <span className="text-lg font-bold">
                  {scoreBreakdown.v2_breakdown
                    ? `${(score * 100).toFixed(1)}%`
                    : score.toString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {generatedSubheading && (
          <div
            className="bg-muted/50 animate-border-highlight rounded-lg border-2 border-transparent p-3"
            key={generatedSubheading}
          >
            <p className="font-medium">Generated Subheading:</p>
            <p className="mt-1">{generatedSubheading}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 p-3">
          <div>
            <p className="font-medium">Sex:</p>
            <p>{mentor.profile?.sex}</p>
          </div>
          <div>
            <p className="font-medium">Race:</p>
            <p>{mentor.profile?.race}</p>
          </div>
          {mentor.profile?.interests && mentor.profile.interests.length > 0 && (
            <div className="col-span-2">
              <p className="font-medium">Interests:</p>
              <p className="truncate">{mentor.profile.interests.join(", ")}</p>
            </div>
          )}
          {mentor.profile?.life_experiences &&
            mentor.profile.life_experiences.length > 0 && (
              <div className="col-span-2">
                <p className="font-medium">Life Experiences:</p>
                <p className="truncate">
                  {mentor.profile.life_experiences.join(", ")}
                </p>
              </div>
            )}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2 p-3">
        <Button
          className="w-full"
          variant="outline"
          onClick={() => setShowDetail(true)}
        >
          View Full Profile
        </Button>
        <Button
          className="w-full"
          variant="default"
          onClick={() => navigate({ to: `/users/${mentor.id}` })}
        >
          Edit User Profile
        </Button>
        <Button
          className="w-full"
          variant="secondary"
          onClick={handleGenerateSubheading}
          disabled={generateSubheading.isPending}
        >
          {generateSubheading.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            "Generate Subheading"
          )}
        </Button>
      </CardFooter>

      <MentorDetail
        mentor={mentor}
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
      />
    </Card>
  );
}

interface MentorDetailProps {
  mentor: User;
  isOpen: boolean;
  onClose: () => void;
}

function MentorDetail({ mentor, isOpen, onClose }: MentorDetailProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mentor Profile</DialogTitle>
        </DialogHeader>

        <MentorDetailInfo
          mentor={{
            name: mentor.name ?? "",
            bio: mentor.profile?.bio || "",
            pronouns: mentor?.pronouns,
            age: mentor?.birthday
              ? (
                  new Date().getFullYear() -
                  new Date(mentor.birthday).getFullYear()
                ).toString()
              : undefined,
            interests: mentor.profile?.interests,
            favoriteSong: mentor.profile?.favorite_music,
            favoriteFood: mentor.profile?.favorite_food,
            videoUrl: mentor.profile?.video_url ?? undefined,
            headshotUrl: mentor?.profile_picture_url ?? undefined,
            life_experiences: mentor.profile?.life_experiences ?? undefined,
            userId: mentor.id,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
